/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useCallback } from 'react';

import {
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useReactToPrint } from 'react-to-print';
import msgNotificacion from '../../scripts/notification';
import LogoFS from '../../assets/images/logoFS.png';
import Formatter from '../../scripts/formatters';
import '../../styles/solicitudes.css';

const totalDisponiblePlan = (planData) => {
  if (!planData) return null;
  if (planData.TotalVacacionesDisponibles != null && planData.TotalVacacionesDisponibles !== '') {
    return Number(planData.TotalVacacionesDisponibles);
  }
  const saldo = planData.VacacionesPendientesInicio != null ? Number(planData.VacacionesPendientesInicio) : 0;
  const proporcion = planData.VacacionesProporcionPeriodo != null ? Number(planData.VacacionesProporcionPeriodo) : 0;
  return saldo + proporcion;
};

const FormPlanVacacionesEmpleadoPdf = ({ data, plan, print, setPrint }) => {
  const componentRef = useRef(null);

  const handleAfterPrint = useCallback(() => {
    msgNotificacion('Impresión exitosa', 'success', 500);
    setPrint(false);
  }, [setPrint]);

  const handleBeforeContent = useCallback(() => {
    msgNotificacion('Imprimiendo...', 'info', 1000);
  }, []);

  const handlePrint = useReactToPrint({
    documentTitle: 'PLAN DE VACACIONES',
    content: () => componentRef.current,
    onAfterPrint: handleAfterPrint,
    onBeforeGetContent: handleBeforeContent,
  });

  const triggerPrint = useCallback(() => {
    if (componentRef.current) {
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
  }, [handlePrint]);

  useEffect(() => {
    if (print) {
      triggerPrint();
    }
  }, [print, triggerPrint]);

  const totalDias = (data || []).reduce((acc, row) => acc + (Number(row.diasEquivalentes) || 0), 0);
  const saldoAntes = plan?.VacacionesPendientesInicio != null ? Number(plan.VacacionesPendientesInicio) : null;
  const proporcion = plan?.VacacionesProporcionPeriodo != null ? Number(plan.VacacionesProporcionPeriodo) : null;
  const totalDisponible = totalDisponiblePlan(plan);
  const vacacionesQuedarian = totalDisponible != null ? totalDisponible - totalDias : null;
  const periodoIni = plan?.PeriodoFechaInicio;
  const periodoFin = plan?.PeriodoFechaFin;

  return (
    <div style={{ padding: 1, margin: 1 }} className="no-print">
      <div ref={componentRef} className="print-container">
        <div className="vh-100 d-flex flex-column align-items-center">
          <div className="d-flex align-items-center justify-content-center my-1 py-4">
            <img src={LogoFS} alt="Logo FS" height={110} width={240} className="me-4" />

            <h4 className="text-center fw-bolder">
              <span>PLAN DE VACACIONES</span>
              {plan?.NombrePeriodo && (
                <p className="no-solicitud">
                  <strong>Período:</strong> {plan.NombrePeriodo}
                </p>
              )}
              {periodoIni && periodoFin && (
                <p className="no-solicitud">
                  <strong>Fechas:</strong>{' '}
                  {Formatter.dateFormatter(periodoIni)} — {Formatter.dateFormatter(periodoFin)}
                </p>
              )}
              {plan?.CodEmpleado && (
                <p className="no-solicitud">
                  <strong>Empleado:</strong> {plan.CodEmpleado}
                  {plan.NombreEmpleado ? ` — ${String(plan.NombreEmpleado).toUpperCase()}` : ''}
                </p>
              )}
              {plan?.Estado && (
                <p className="no-solicitud">
                  <strong>Estado del plan:</strong> {plan.Estado}
                </p>
              )}
            </h4>
          </div>

          {plan?.Motivo && (
            <Grid container spacing={2} className="px-5 border py-3 mb-3">
              <Grid item xs={4} md={3}>
                <Typography fontWeight="bold">Motivo / observaciones:</Typography>
              </Grid>
              <Grid item xs={8} md={9}>
                <Typography>{plan.Motivo}</Typography>
              </Grid>
            </Grid>
          )}

          <TableContainer component={Paper} className="mt-2" sx={{ width: '100%', maxWidth: 900 }}>
            <Table size="small" aria-label="plan vacaciones empleado">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    #
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    FECHA
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    DÍA
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    JORNADA
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                    DÍAS EQ.
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ border: '1px solid #ddd', py: 3 }}>
                      Sin días planificados
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row, index) => (
                    <TableRow key={row.fecha}>
                      <TableCell align="center" sx={{ border: '1px solid #ddd' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #ddd' }}>
                        {Formatter.dateFormatter(row.fecha)}
                      </TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #ddd' }}>
                        {row.diaSemana}
                      </TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #ddd' }}>
                        {row.tipoLabel}
                      </TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #ddd' }}>
                        {Number(row.diasEquivalentes).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <div className="mt-3 w-100 text-center">
            {totalDisponible != null && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Saldo antes del período:</strong> {(saldoAntes ?? 0).toFixed(2)}
                {' '}
                + <strong>Proporción:</strong> {(proporcion ?? 0).toFixed(2)}
                {' '}
                = <strong>Total disponible:</strong> {totalDisponible.toFixed(2)}
              </Typography>
            )}
            <Typography
              sx={vacacionesQuedarian != null && vacacionesQuedarian < 0 ? { color: 'error.main' } : undefined}
            >
              {totalDisponible != null ? (
                <>
                  <strong>Total disponible:</strong> {totalDisponible.toFixed(2)}
                  {' '}
                  - <strong>Total planificado:</strong> {totalDias.toFixed(2)}
                  {' '}
                  = <strong>Quedarían:</strong>{' '}
                  {vacacionesQuedarian != null ? vacacionesQuedarian.toFixed(2) : '—'}
                </>
              ) : (
                <>
                  <strong>Total planificado:</strong> {totalDias.toFixed(2)}
                </>
              )}
            </Typography>
          </div>

          <Grid container spacing={2} className="px-5 py-5 mt-4 border-top justify-content-between w-100">
            <Grid item xs={4} md={4} textAlign="center">
              <Typography className="text-decoration-underline" fontSize={10}>
                {plan?.NombreEmpleado || '_________________'}
              </Typography>
              <Typography fontWeight="bolder">Solicita</Typography>
            </Grid>
            <Grid item xs={4} md={4} textAlign="center">
              <Typography className="text-decoration-underline" fontSize={10}>
                {plan?.JefeInmediato || '_________________'}
              </Typography>
              <Typography fontWeight="bolder">Autoriza</Typography>
            </Grid>
            <Grid item xs={4} md={4} textAlign="center">
              <Typography className="text-decoration-underline" fontSize={10}>
                {plan?.JefeRRHH || '_________________'}
              </Typography>
              <Typography fontWeight="bolder">Recibe RRHH</Typography>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default FormPlanVacacionesEmpleadoPdf;
